/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import * as satellite from 'satellite.js';
import {
  EcefPosition,
  GeoLocation,
  GroundStationAvailability,
  GroundStationAvailabilityDto,
  GroundStationData,
  GroundStationDataDto,
  GroundStationReference,
  GroundStationReferenceDto,
  GroundStationType,
  OrbitPoint,
  OrbitPointDto,
  OrbitPointsBySvName,
  OrbitPointsBySvNameDto,
  OrbitPosition,
  PositionType,
  Reservation,
  ReservationDto,
  ReservationStatusType,
  SpaceVehicleIdDto,
  SpaceVehicleId,
  SpaceVehiclePosition,
  SpaceVehiclePositionsBySvName,
  TimeWindow,
  TimeWindowDto,
} from '../shared/entities/haloNet';

const METERS_TO_FEET = 3.28083989501312;
const METERS_TO_KILOMETERS = 0.001;
const KILOMETERS_TO_METERS = 1000;
const DEFAULT_TIME_EPOCH = new Date('1970-01-01T00:00:00Z');
const RADIANS_TO_DEGREES = 180 / Math.PI;

const radiansToDegrees = (radians: number): number => radians * RADIANS_TO_DEGREES;

export const metersToFeet = (meters: number): number => meters * METERS_TO_FEET;

export const parseIsoDate = (isoDate: string): Date => new Date(isoDate);

const toGroundStationType = (stationType: string | GroundStationType | undefined): GroundStationType => {
  if (stationType === GroundStationType.GROUND_STATION_TYPE_RTE) {
    return GroundStationType.GROUND_STATION_TYPE_RTE;
  }

  return GroundStationType.GROUND_STATION_TYPE_UNSPECIFIED;
};

const toReservationStatusType = (status: string | ReservationStatusType | undefined): ReservationStatusType => {
  if (!status) {
    return ReservationStatusType.RESERVATION_STATUS_TYPE_UNSPECIFIED;
  }

  const statuses = Object.values(ReservationStatusType) as string[];
  if (statuses.includes(status)) {
    return status as ReservationStatusType;
  }

  return ReservationStatusType.RESERVATION_STATUS_TYPE_UNSPECIFIED;
};

const toDate = (date: string | Date): Date => (date instanceof Date ? date : parseIsoDate(date));

const normalizeTimeWindow = (window: TimeWindowDto): TimeWindow => ({
  start: toDate(window.start),
  end: toDate(window.end),
});

const normalizeGroundStationRef = (groundStation: GroundStationReferenceDto): GroundStationReference => ({
  stationName: groundStation.stationName,
  stationType: toGroundStationType(groundStation.stationType),
});

const normalizeEcefPosition = (ecefPosition: EcefPosition, referenceDate: Date = DEFAULT_TIME_EPOCH): GeoLocation => {
  const gmst = satellite.gstime(referenceDate as any);
  const ecfInKilometers = {
    x: ecefPosition.xMeters * METERS_TO_KILOMETERS,
    y: ecefPosition.yMeters * METERS_TO_KILOMETERS,
    z: ecefPosition.zMeters * METERS_TO_KILOMETERS,
  };

  const eci = satellite.ecfToEci(ecfInKilometers as any, gmst as any);
  const geodetic = satellite.eciToGeodetic(eci as any, gmst as any);

  return {
    latitude: radiansToDegrees((geodetic as any).latitude),
    longitude: radiansToDegrees((geodetic as any).longitude),
    altitudeFeet: metersToFeet((geodetic as any).height * KILOMETERS_TO_METERS),
    name: ecefPosition.name,
  };
};

const normalizeGroundStationPosition = (
  position: OrbitPosition,
  referenceDate: Date = DEFAULT_TIME_EPOCH,
): GeoLocation => {
  if (position.positionType === PositionType.LAT_LON_HEIGHT) {
    return {
      latitude: position.latitude,
      longitude: position.longitude,
      altitudeFeet: metersToFeet(position.altitudeMeters),
      name: position.name,
    };
  }

  const gmst = satellite.gstime(referenceDate as any);
  const ecfInKilometers = {
    x: position.xMeters * METERS_TO_KILOMETERS,
    y: position.yMeters * METERS_TO_KILOMETERS,
    z: position.zMeters * METERS_TO_KILOMETERS,
  };

  const eci = satellite.ecfToEci(ecfInKilometers as any, gmst as any);
  const geodetic = satellite.eciToGeodetic(eci as any, gmst as any);

  return {
    latitude: radiansToDegrees((geodetic as any).latitude),
    longitude: radiansToDegrees((geodetic as any).longitude),
    altitudeFeet: metersToFeet((geodetic as any).height * KILOMETERS_TO_METERS),
    name: position.name,
  };
};

export const normalizePosition = (position: OrbitPosition, referenceDate: Date = DEFAULT_TIME_EPOCH): GeoLocation => {
  if (position.positionType === PositionType.ECEF_POSITION) {
    return normalizeEcefPosition(position, referenceDate);
  }

  return {
    latitude: position.latitude,
    longitude: position.longitude,
    altitudeFeet: metersToFeet(position.altitudeMeters),
    name: position.name,
  };
};

export const normalizeHaloNetGroundStations = (groundStations: GroundStationDataDto[]): GroundStationData[] => {
  return (groundStations || []).map(groundStation => ({
    groundStationName: groundStation.groundStationName,
    location: normalizeGroundStationPosition(groundStation.location),
    type: toGroundStationType(groundStation.type),
    downlinkOnly: groundStation.downlinkOnly,
  }));
};

export const normalizeHaloNetGroundStationAvailabilities = (
  availabilities: GroundStationAvailabilityDto[],
): GroundStationAvailability[] => {
  return (availabilities || []).map(availability => ({
    groundStation: normalizeGroundStationRef(availability.groundStation),
    svName: availability.svName,
    availabilityWindows: (availability.availabilityWindows || []).map(normalizeTimeWindow),
  }));
};

export const normalizeHaloNetGroundStationReservations = (reservations: ReservationDto[]): Reservation[] => {
  return (reservations || []).map(reservation => ({
    reservationId: reservation.reservationId,
    svName: reservation.svName,
    groundStation: normalizeGroundStationRef(reservation.groundStation),
    timeWindow: normalizeTimeWindow(reservation.timeWindow),
    status: toReservationStatusType(reservation.status),
    prepassSeconds: reservation.prepassSeconds,
    postpassSeconds: reservation.postpassSeconds,
    configuration: reservation.configuration,
  }));
};

export const normalizeHaloNetSpaceVehicles = (spaceVehicles: SpaceVehicleIdDto[]): SpaceVehicleId[] => {
  return (spaceVehicles || []).map(spaceVehicle => ({
    svName: spaceVehicle.svName,
  }));
};

const normalizeHaloNetOrbitPoint = (orbitPoint: OrbitPointDto): OrbitPoint => {
  const sourceTime = toDate(orbitPoint.sourceTime);

  return {
    sourceTime,
    position: normalizePosition(orbitPoint.position, sourceTime),
  };
};

export const normalizeHaloNetSpaceVehicleOrbits = (orbits: OrbitPointsBySvNameDto): OrbitPointsBySvName => {
  return Object.entries(orbits || {}).reduce((accumulator: OrbitPointsBySvName, [svName, orbitPoints]) => {
    accumulator[svName] = (orbitPoints || []).map(normalizeHaloNetOrbitPoint);
    return accumulator;
  }, {});
};

export const MS_TO_SECONDS = 0.001;

const toSpaceVehiclePosition = (orbitPoint: OrbitPoint, isOutsidePropagationWindow: boolean): SpaceVehiclePosition => ({
  latitudeDeg: orbitPoint.position.latitude,
  longitudeDeg: orbitPoint.position.longitude,
  altitudeFeet: orbitPoint.position.altitudeFeet,
  sourceTime: Math.round(orbitPoint.sourceTime.getTime() * MS_TO_SECONDS),
  isOutsidePropagationWindow,
});

/**
 * Find the propagated point that is nearest to the given time.
 * The points are already sorted by time and already converted to geodetic coordinates,
 * so this is a lookup and not a calculation.
 * A time outside the propagation window gives the nearest end point, flagged as outside the window.
 */
export const getSpaceVehiclePositionAtTime = (
  orbitPoints: OrbitPoint[],
  atTime: Date,
): SpaceVehiclePosition | undefined => {
  if (!orbitPoints || orbitPoints.length === 0) {
    return undefined;
  }

  const atTimeMs = atTime.getTime();
  const firstPoint = orbitPoints[0];
  const lastPoint = orbitPoints[orbitPoints.length - 1];

  if (atTimeMs <= firstPoint.sourceTime.getTime()) {
    return toSpaceVehiclePosition(firstPoint, atTimeMs < firstPoint.sourceTime.getTime());
  }
  if (atTimeMs >= lastPoint.sourceTime.getTime()) {
    return toSpaceVehiclePosition(lastPoint, atTimeMs > lastPoint.sourceTime.getTime());
  }

  // Binary search for the last point at or before atTime.
  let low = 0;
  let high = orbitPoints.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (orbitPoints[middle].sourceTime.getTime() <= atTimeMs) {
      low = middle;
    } else {
      high = middle;
    }
  }

  const isNearerToHigh =
    orbitPoints[high].sourceTime.getTime() - atTimeMs < atTimeMs - orbitPoints[low].sourceTime.getTime();

  return toSpaceVehiclePosition(isNearerToHigh ? orbitPoints[high] : orbitPoints[low], false);
};

/**
 * Read the spacing of the propagated points, in milliseconds.
 *
 * halonet-service sets the spacing from propagatedStatesTimeStepSecs, which lives in config-repo
 * and can change without the portal being rebuilt. Reading it back off the points keeps the two
 * in step on their own, and reports what the upstream propagator actually returned rather than
 * what was asked for.
 *
 * Returns undefined when no space vehicle has two points to measure between.
 */
export const getPropagatedStateStepMs = (orbitsBySvName: OrbitPointsBySvName): number | undefined => {
  const measurablePoints = Object.values(orbitsBySvName || {}).find(
    (orbitPoints: OrbitPoint[]) => orbitPoints && orbitPoints.length >= 2,
  );

  if (!measurablePoints) {
    return undefined;
  }

  const stepMs = measurablePoints[1].sourceTime.getTime() - measurablePoints[0].sourceTime.getTime();

  return stepMs > 0 ? stepMs : undefined;
};

/**
 * Find the position of every space vehicle at the given time.
 * Space vehicles with no propagated points are left out.
 */
export const getSpaceVehiclePositionsAtTime = (
  orbitsBySvName: OrbitPointsBySvName,
  atTime: Date,
): SpaceVehiclePositionsBySvName => {
  return Object.entries(orbitsBySvName || {}).reduce(
    (accumulator: SpaceVehiclePositionsBySvName, [svName, orbitPoints]) => {
      const position = getSpaceVehiclePositionAtTime(orbitPoints, atTime);
      if (position) {
        accumulator[svName] = position;
      }
      return accumulator;
    },
    {},
  );
};
